import WebView from 'react-native-webview'
import { useMemo, memo } from 'react'
import { getToken } from 'tamagui'
import type { IUpdateLessonProgress } from '@components/MediaRender'
import { buildVimeoHtml } from './template'
import { useVimeoVideoPlayer } from './useVimeoVideoPlayer'

export interface IVimeoVideoPlayerProps {
  videoId: string
  // Privacy hash for unlisted videos; required in the embed URL or the player 403s.
  videoHash?: string
  updateProgress: (params: IUpdateLessonProgress) => void
  videoStartTime?: number
  actualProgress?: number
  domain: string
}

function VimeoVideoPlayerComponent({
  videoId,
  videoHash,
  updateProgress,
  videoStartTime,
  actualProgress = 0,
  domain,
}: IVimeoVideoPlayerProps) {
  const videoBorderRadius = getToken('$border-radius-md')
  const { handleMessage, handleLoadStart } = useVimeoVideoPlayer({
    actualProgress,
    updateProgress,
  })

  const html = useMemo(() => {
    const borderRadiusPx = (() => {
      try {
        const value = String(videoBorderRadius).replace(/px$/, '')
        const parsed = Number(value)
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
      } catch {
        return 0
      }
    })()

    return buildVimeoHtml({
      videoId,
      hash: videoHash,
      startSeconds: Math.max(0, Number(videoStartTime ?? 0)),
      borderRadiusPx,
    })
  }, [videoId, videoHash, videoStartTime, videoBorderRadius])

  return (
    <WebView
      source={{ html, baseUrl: domain }}
      allowsInlineMediaPlayback={true}
      allowsFullscreenVideo={true}
      mediaPlaybackRequiresUserAction={false}
      javaScriptEnabled={true}
      domStorageEnabled={true}
      injectedJavaScript="true;"
      onMessage={handleMessage}
      onLoadStart={handleLoadStart}
      style={{
        borderRadius: videoBorderRadius,
        backgroundColor: 'transparent',
      }}
    />
  )
}

VimeoVideoPlayerComponent.displayName = 'VimeoVideoPlayer'

export const VimeoVideoPlayer = memo(VimeoVideoPlayerComponent)
